import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { routeUtil, SubNavigation } from '@cmg/common';
import React from 'react';
import { connect } from 'react-redux';
import { Route, Routes, useParams } from 'react-router-dom';
import styled from 'styled-components';

import routeFactory from '../../common/util/routeFactory';
import type { UUID } from '../../types/common';
import { selectCrmInvestorFirm } from './crm-match-list/ducks';
import { selectCrmIntegration } from './shared/ducks';

const mapStateToProps = state => ({
  crmIntegration: selectCrmIntegration(state),
  crmInvestorFirm: selectCrmInvestorFirm(state),
});

type StateProps = ReturnType<typeof mapStateToProps>;

export const StyledSubNavigationLink = styled(SubNavigation.Link)`
  margin-left: 2rem;
`;

/**
 * Rolodex CRM Integration Breadcrumb
 */
export const RolodexCrmIntegrationBreadcrumbComponent: React.FC<StateProps> = ({
  crmIntegration,
}) => {
  const { crmIntegrationId } = useParams<{ crmIntegrationId: UUID }>();
  if (!crmIntegration || !crmIntegrationId) {
    return null;
  }

  return (
    <React.Fragment>
      <SubNavigation.Breadcrumb>
        <SubNavigation.Link
          end
          to={routeFactory.rolodexCrmIntegrationDetail.getUrlPath({ crmIntegrationId })}
        >
          {crmIntegration.name}
        </SubNavigation.Link>
      </SubNavigation.Breadcrumb>
    </React.Fragment>
  );
};

const ConnectedIntegrationBreadcrumb = connect(mapStateToProps)(
  RolodexCrmIntegrationBreadcrumbComponent
);

/**
 * Rolodex CRM Management Breadcrumb
 */
export const RolodexCrmManagementBreadcrumbComponent: React.FC<StateProps> = ({
  crmIntegration,
}) => {
  const { crmIntegrationId } = useParams<{ crmIntegrationId: UUID }>();
  const canManageGlobalCrm = useCheckPermissions([permissionsByEntity.GlobalCrm.FULL]);

  if (!crmIntegration || !crmIntegrationId) {
    return null;
  }

  return (
    <React.Fragment>
      {canManageGlobalCrm && crmIntegrationId && (
        <StyledSubNavigationLink
          to={routeFactory.sysAdminCrmManagement.getUrlPath({ crmIntegrationId })}
        >
          CRM Management
        </StyledSubNavigationLink>
      )}
    </React.Fragment>
  );
};

const ConnectedCRMManagementBreadcrumb = connect(mapStateToProps)(
  RolodexCrmManagementBreadcrumbComponent
);

/**
 * Rolodex CRM Investor Firm Breadcrumb
 */
export const RolodexCrmInvestorFirmBreadcrumbComponent: React.FC<StateProps> = ({
  crmInvestorFirm,
}) => {
  const { crmIntegrationId, crmInvestorFirmId } = useParams<{
    crmIntegrationId: UUID;
    crmInvestorFirmId: UUID;
  }>();

  if (!crmInvestorFirm || !crmIntegrationId || !crmInvestorFirmId) {
    return null;
  }

  return (
    <React.Fragment>
      <SubNavigation.Breadcrumb>
        <SubNavigation.Link
          to={routeFactory.rolodexCrmEntityMatcher.getUrlPath({
            crmIntegrationId,
            crmInvestorFirmId,
          })}
        >
          {crmInvestorFirm.name}
        </SubNavigation.Link>
      </SubNavigation.Breadcrumb>
    </React.Fragment>
  );
};

const ConnectedInvestorFirmBreadcrumb = connect(mapStateToProps)(
  RolodexCrmInvestorFirmBreadcrumbComponent
);

/**
 * Displays the CRM Integration's name as breadcrumb on CRM Integration Details
 * Displays the CRM Investor Firm name when on CRM Entity Matcher (Investor Firm Details)
 *
 * Used within Rolodex sub nav.
 */
const rel = (routePath: string) =>
  routeUtil.getRelativeRoutePath(routeFactory.rolodex.routePath, routePath);

const RolodexCrmBreadcrumb = () => {
  return (
    <Routes>
      <Route
        path={rel(routeFactory.rolodexCrmIntegrationDetail.routePath)}
        element={
          <React.Fragment>
            <ConnectedIntegrationBreadcrumb />
            <ConnectedCRMManagementBreadcrumb />
          </React.Fragment>
        }
      />
      <Route
        path={`${rel(routeFactory.rolodexCrmEntityMatcher.routePath)}/*`}
        element={
          <React.Fragment>
            <ConnectedIntegrationBreadcrumb />
            <ConnectedInvestorFirmBreadcrumb />
            <ConnectedCRMManagementBreadcrumb />
          </React.Fragment>
        }
      />
    </Routes>
  );
};

export default RolodexCrmBreadcrumb;
